/**
 * Feature Flag Modal Component
 *
 * Dialog for creating and editing feature flags.
 * Supports scope-based target selection.
 */
import { useState, useEffect } from 'react';
import {
  cn,
  Button,
  Input,
  Label,
  Switch,
  Textarea,
} from '@respectly/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@respectly/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@respectly/ui';
import { Flag, Loader2, Info } from 'lucide-react';
import type {
  FeatureFlag,
  FeatureFlagScope,
  CreateFeatureFlagDto,
  UpdateFeatureFlagDto,
} from '@respectly/shared-types';
import { getScopeOptions, getScopeDescription } from './FeatureFlagScopeBadge';

// ============================================
// Form State
// ============================================

interface FormState {
  name: string;
  key: string;
  description: string;
  scope: FeatureFlagScope;
  enabled: boolean;
  targetPlans: string[];
  targetTenants: string[];
}

const initialFormState: FormState = {
  name: '',
  key: '',
  description: '',
  scope: 'global',
  enabled: false,
  targetPlans: [],
  targetTenants: [],
};

// ============================================
// Component Props
// ============================================

export interface FeatureFlagModalProps {
  flag?: FeatureFlag | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    data: CreateFeatureFlagDto | UpdateFeatureFlagDto
  ) => Promise<boolean>;
  isLoading?: boolean;
  plans?: { id: string; name: string }[];
  tenants?: { id: string; name: string }[];
}

// ============================================
// Helper Functions
// ============================================

function generateKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 50);
}

// ============================================
// Component
// ============================================

export function FeatureFlagModal({
  flag,
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
  plans = [],
  tenants = [],
}: FeatureFlagModalProps) {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [autoGenerateKey, setAutoGenerateKey] = useState(true);

  const isEditing = !!flag;
  const scopeOptions = getScopeOptions();

  // Reset form when modal opens/closes or flag changes
  useEffect(() => {
    if (open && flag) {
      setForm({
        name: flag.name,
        key: flag.key,
        description: flag.description ?? '',
        scope: flag.scope,
        enabled: flag.enabled,
        targetPlans: flag.targetPlans ?? [],
        targetTenants: flag.targetTenants ?? [],
      });
      setAutoGenerateKey(false);
    } else if (open) {
      setForm(initialFormState);
      setAutoGenerateKey(true);
    }
    setErrors({});
  }, [open, flag]);

  // Auto-generate key from name
  useEffect(() => {
    if (autoGenerateKey && !isEditing) {
      setForm((prev) => ({ ...prev, key: generateKey(prev.name) }));
    }
  }, [form.name, autoGenerateKey, isEditing]);

  // Validate form
  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};

    if (!form.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!form.key.trim()) {
      newErrors.key = 'Key is required';
    } else if (!/^[a-z0-9_]+$/.test(form.key)) {
      newErrors.key = 'Key must be lowercase letters, numbers, and underscores only';
    }

    if (form.scope === 'plan' && form.targetPlans.length === 0) {
      newErrors.targetPlans = 'Select at least one plan';
    }

    if (form.scope === 'tenant' && form.targetTenants.length === 0) {
      newErrors.targetTenants = 'Select at least one tenant';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const data: CreateFeatureFlagDto | UpdateFeatureFlagDto = {
      name: form.name,
      key: form.key,
      description: form.description || undefined,
      scope: form.scope,
      enabled: form.enabled,
      targetPlans: form.scope === 'plan' ? form.targetPlans : undefined,
      targetTenants: form.scope === 'tenant' ? form.targetTenants : undefined,
    };

    const success = await onSubmit(data);
    if (success) {
      onOpenChange(false);
    }
  };

  // Handle input change
  const handleChange = <K extends keyof FormState>(
    key: K,
    value: FormState[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  // Handle key manual edit
  const handleKeyChange = (value: string) => {
    setAutoGenerateKey(false);
    handleChange('key', value.toLowerCase().replace(/[^a-z0-9_]/g, ''));
  };

  // Handle scope change
  const handleScopeChange = (scope: FeatureFlagScope) => {
    handleChange('scope', scope);
    // Clear targets when scope changes
    setForm((prev) => ({
      ...prev,
      scope,
      targetPlans: [],
      targetTenants: [],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
              <Flag className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <DialogTitle>
                {isEditing ? 'Edit Feature Flag' : 'Create Feature Flag'}
              </DialogTitle>
              <DialogDescription>
                {isEditing
                  ? 'Update the feature flag configuration.'
                  : 'Create a new feature flag to control feature rollout.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="New Dashboard"
              className={cn(errors.name && 'border-red-500')}
            />
            {errors.name && (
              <p className="text-xs text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Key */}
          <div className="space-y-2">
            <Label htmlFor="key">Key</Label>
            <Input
              id="key"
              value={form.key}
              onChange={(e) => handleKeyChange(e.target.value)}
              placeholder="new_dashboard"
              className={cn(
                errors.key && 'border-red-500',
                'font-mono text-sm'
              )}
            />
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Used in code to check flag status. Lowercase with underscores only.
            </p>
            {errors.key && (
              <p className="text-xs text-red-500">{errors.key}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe what this feature flag controls..."
              rows={2}
            />
          </div>

          {/* Scope */}
          <div className="space-y-2">
            <Label htmlFor="scope">Scope</Label>
            <Select
              value={form.scope}
              onValueChange={(value) => handleScopeChange(value as FeatureFlagScope)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select scope" />
              </SelectTrigger>
              <SelectContent>
                {scopeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex flex-col">
                      <span>{option.label}</span>
                      <span className="text-xs text-slate-500">
                        {option.description}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {getScopeDescription(form.scope)}
            </p>
          </div>

          {/* Plan targets */}
          {form.scope === 'plan' && (
            <div className="space-y-2">
              <Label>Target Plans</Label>
              {plans.length > 0 ? (
                <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3">
                  {plans.map((plan) => (
                    <label
                      key={plan.id}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={form.targetPlans.includes(plan.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleChange('targetPlans', [...form.targetPlans, plan.id]);
                          } else {
                            handleChange(
                              'targetPlans',
                              form.targetPlans.filter((id) => id !== plan.id)
                            );
                          }
                        }}
                        className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                      />
                      <span className="text-sm">{plan.name}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm text-slate-500">
                  <Info className="w-4 h-4" />
                  <span>No plans available. Add plan IDs manually.</span>
                </div>
              )}
              {errors.targetPlans && (
                <p className="text-xs text-red-500">{errors.targetPlans}</p>
              )}
            </div>
          )}

          {/* Tenant targets */}
          {form.scope === 'tenant' && (
            <div className="space-y-2">
              <Label>Target Tenants</Label>
              {tenants.length > 0 ? (
                <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3">
                  {tenants.map((tenant) => (
                    <label
                      key={tenant.id}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={form.targetTenants.includes(tenant.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleChange('targetTenants', [
                              ...form.targetTenants,
                              tenant.id,
                            ]);
                          } else {
                            handleChange(
                              'targetTenants',
                              form.targetTenants.filter((id) => id !== tenant.id)
                            );
                          }
                        }}
                        className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-sm">{tenant.name}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm text-slate-500">
                  <Info className="w-4 h-4" />
                  <span>No tenants available. Add tenant IDs manually.</span>
                </div>
              )}
              {errors.targetTenants && (
                <p className="text-xs text-red-500">{errors.targetTenants}</p>
              )}
            </div>
          )}

          {/* Enabled status */}
          <div className="flex items-center justify-between py-2">
            <div>
              <Label htmlFor="enabled" className="font-medium">
                Enable Flag
              </Label>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Turn on the feature for the selected scope
              </p>
            </div>
            <Switch
              id="enabled"
              checked={form.enabled}
              onCheckedChange={(checked) => handleChange('enabled', checked)}
            />
          </div>
        </form>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isEditing ? 'Updating...' : 'Creating...'}
              </>
            ) : isEditing ? (
              'Update Flag'
            ) : (
              'Create Flag'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
